import { Stack } from "@mantine/core";
import { GraphQL } from "@rpi/openapi-api";
import { useShowSuccessToast } from "@rpi/openapi-core";
import React, { createContext, useContext } from "react";
import { Outlet } from "react-router-dom";
import { RpaDrawer } from "../../rpi-core/drawer/RpaDrawer";
import { ConfirmCancelModal } from "./ConfirmCancelModal";

export const path = "create";

type ProductCreateContextType = {
  loading: boolean;
  onSave: (name: string, customerSku: string, openapiProductId: number, onError: (error: unknown) => void) => void;
  onClose: () => void;
};
const ProductCreateContext = createContext<ProductCreateContextType | undefined>(undefined);

export function useProductCreateContext() {
  const context = useContext(ProductCreateContext);
  if (!context) {
    throw new Error("Must be used within a ProductCreateContext Provider");
  }
  return context;
}

type CustomerProductCreateViewProps = {
  isOpened: boolean;
  onClose: () => void;
};
export function CustomerProductCreateView({ isOpened, onClose }: CustomerProductCreateViewProps) {
  const showSuccessToast = useShowSuccessToast();
  const { mutate: tryCreate, isLoading: loading } = GraphQL.useCreateCustomerProductMutation({
    onSuccess() {
      showSuccessToast("Your product was added successfully!");
      onClose();
    },
  });

  function onSave(name: string, customerSku: string, openapiProductId: number, onError: (error: unknown) => void) {
    tryCreate(
      { input: { name, customerSku, openapiProductId } },
      {
        onError,
      }
    );
  }

  const [showConfirmModal, setShowConfirmModal] = React.useState(false);

  const handleDrawerClose = () => {
    // overrides closing behavior entirely
    // when loading, nothing happens on the close event
    if (!loading) {
      // otherwise, shows the confirm modal
      setShowConfirmModal(true);
    }
  };

  const handleConfirmClose = () => {
    setShowConfirmModal(false);
    onClose();
  };

  const handleCancelModal = () => {
    setShowConfirmModal(false);
  };

  return (
    <RpaDrawer title="Add New Product" opened={isOpened} onClose={handleDrawerClose} data-testid="addNewProductDrawer">
      <Stack sx={(theme) => ({ padding: `0 ${theme.spacing.lg}px` })}>
        <ProductCreateContext.Provider value={{ onSave, loading, onClose: handleDrawerClose }}>
          <Outlet />
        </ProductCreateContext.Provider>
        <ConfirmCancelModal
          opened={isOpened && showConfirmModal}
          onClose={handleCancelModal}
          onConfirm={handleConfirmClose}
        />
      </Stack>
    </RpaDrawer>
  );
}
